import createTxpConfiguration from './createTxpConfiguration';
import getButtonsEditControl from './getButtonsEditControl';
import getDetailsEditControl from './getDetailsEditControl';
import getEditableActionButtonProps from './getEditableActionButtonProps';
import getSummaryEditControl from './getSummaryEditControl';
import getTxpContextMenuItems from './getTxpContextMenuItems';
import { populateMailImages } from '../../utils/view/populateImages';
import { PerformanceDatapoint } from 'owa-analytics';
import { getPackageBaseUrl } from 'owa-config';
import { CardItem, TxpDatapoint } from 'txp-data';
import TxpConfiguration from 'txp-data/lib/schema/configSchema/TxpConfiguration';
import { CardMode } from 'txp-data/lib/schema/viewSchema/CardMode';
import TxpDataRow from 'txp-data/lib/schema/viewSchema/TxpDataRow';
import TxpStyle from 'txp-data/lib/schema/viewSchema/TxpStyle';
import PostalAddress from 'txp-data/lib/schema/innerSchema/PostalAddress';

export default function createMailTxpConfiguration(
    isEditableCardSupported: boolean,
    isInvoiceSupported: boolean,
    entityNamesMap: number,
    isServiceReservationSupported: boolean,
    onReportIssueClicked?: (feedbackType: string, feedbackLocationName: string) => void,
    onEventsFromEmailSettingsClicked?: (cardItem: CardItem) => void,
    onAddToCalendarClicked?: () => void,
    onRemoveFromCalendarClicked?: () => void,
    onSaveEditableCard?: (
        cardItem: CardItem,
        perfDataPoint: PerformanceDatapoint,
        summaryEditableField?: string,
        editableLocation?: PostalAddress,
        onError?: (clientErrorMessage: string, status: any, error?: Error) => void,
        onSuccess?: () => void
    ) => void,
    getCalendarDeepLinkUrl?: (calendarEventId: string) => string,
    datapointLogger?: (
        txpDatapoint: TxpDatapoint,
        datapointName: string,
        internetMessageId?: string
    ) => void,
    getPerfDatapointLogger?: (datapointName: string) => PerformanceDatapoint,
    getAriaText?: (cardItem: CardItem, strings?: string[]) => string,
    getDisplayAddress?: (location: PostalAddress) => string
): TxpConfiguration {
    let txpConfig = createTxpConfiguration(
        isInvoiceSupported,
        entityNamesMap,
        true, // showActionButtons is true for mail
        isServiceReservationSupported,
        null, // cardRole is set to null for mail
        isEditableCardSupported,
        datapointLogger,
        null, // this argument is only applicable for TxpPeekwrapper; callback to show the mail popout from calendar
        null, // this argument is only applicable for TxpPeekwrapper and TxpCalendarFormWrapper; location control
        onReportIssueClicked,
        onEventsFromEmailSettingsClicked,
        onAddToCalendarClicked,
        onRemoveFromCalendarClicked,
        getCalendarDeepLinkUrl
    );

    txpConfig.txpImages = populateMailImages();
    txpConfig.txpImages.packageBaseUrl = getPackageBaseUrl;
    txpConfig.getContextMenuItems = getTxpContextMenuItems;
    txpConfig.getPerfDatapointLogger = getPerfDatapointLogger;

    // Callbacks for Editable cards controls

    if (isEditableCardSupported) {
        txpConfig.onSaveEditableCard = onSaveEditableCard;
        txpConfig.getActionButtonProperties = getEditableActionButtonProps;
        txpConfig.getSummaryEditControl = (
            cardMode: CardMode,
            readOnlyLines: TxpDataRow[],
            editableField: string,
            editableData: TxpDataRow,
            onTextChanged?: (value: string) => void
        ) =>
            getSummaryEditControl(
                cardMode,
                readOnlyLines,
                editableField,
                editableData,
                onTextChanged
            );

        txpConfig.getDetailsEditControl = (
            cardMode: CardMode,
            txpStyle: TxpStyle,
            readOnlyLines: TxpDataRow[],
            editableText: string,
            editableData: TxpDataRow,
            location: PostalAddress,
            onLocationChanged?: (location: PostalAddress, stringAddress: string) => void,
            getDisplayAddress?: (location: PostalAddress) => string
        ) =>
            getDetailsEditControl(
                cardMode,
                txpStyle,
                readOnlyLines,
                editableText,
                editableData,
                location,
                onLocationChanged,
                getDisplayAddress
            );

        txpConfig.getButtonsEditControl = (
            onSaveClicked: () => void,
            onDiscardClicked: () => void,
            cardMode: CardMode,
            clientErrorMessage: string
        ) => getButtonsEditControl(onSaveClicked, onDiscardClicked, cardMode, clientErrorMessage);

        txpConfig.getAriaText = (cardItem: CardItem, strings?: string[]) =>
            getAriaText(cardItem, strings);

        txpConfig.getDisplayAddress = (location: PostalAddress) => getDisplayAddress(location);
    }
    return txpConfig;
}
